import { motion } from 'framer-motion';
import ConnectModal from './connectmodal';
import { useContext, useEffect } from 'react';
import Web3Context from './context/web3context';
import Web3 from 'web3';
import ScreenWidthContext from './context/screenwidthcontext';
import { Link, useLocation } from 'react-router-dom';
import { getScratchBalance } from './components/utils/scratch-functions';
import 'react-toastify/dist/ReactToastify.css';
import { webStorage } from './config';
import { exploreObjects } from './frontpage/frontObjs';

const socialLinks = [
    {
        href: 'https://discord.gg/boomer-squad-nft-939820143486320650',
        icon: '/navbar/discordlogo.png',
        label: 'Discord',
    },
    {
        href: 'https://twitter.com/BoomerSquadNFT1',
        icon: '/navbar/twitterlogo.png',
        label: 'Twitter',
    },
    {
        href: 'https://medium.com/@BoomerSquadNFT',
        icon: '/navbar/mediumlogo.png',
        label: 'Medium',
    },
];

const externalLinks = [
    {
        href: 'https://boomer-squad.gitbook.io/boomer-squad-nft/',
        label: 'Docs',
        accent: 'cyan',
    },
];

const mobileNavItems = [
    {
        href: '/',
        label: 'Home',
    },
    {
        href: '/myboomers',
        label: 'Boomers',
    },
    {
        href: '/boomrooms',
        label: 'Rooms',
    },
    {
        href: '/myzoomers',
        label: 'Zoomers',
    },
];

function NavBar() {
    const location = useLocation();
    const { screenWidth, setScreenWidth } = useContext(ScreenWidthContext);
    const { setAccount, setBalance, setChainID, setScratchBalance } = useContext(Web3Context);

    const isDesktop = screenWidth > 1279;
    const isCompactDesktop = isDesktop && screenWidth < 1700;

    useEffect(() => {
        const handleWindowResize = () => setScreenWidth(window.innerWidth);
        handleWindowResize();
        window.addEventListener('resize', handleWindowResize);

        return () => window.removeEventListener('resize', handleWindowResize);
    }, [setScreenWidth]);

    useEffect(() => {
        const { ethereum } = window as any;

        function handleAccountsChanged(accounts: string[]) {
            if (accounts.length === 0) {
                window.location.reload();
                return;
            }

            const web3 = new Web3(Web3.givenProvider || 'ws://localhost:8545');
            const nextAccount = accounts[0];

            setAccount(nextAccount);

            web3.eth.getBalance(nextAccount, function (err, result) {
                if (!err) {
                    setBalance(web3.utils.fromWei(result, 'ether'));
                }
            });

            getScratchBalance(Web3.givenProvider || 'ws://localhost:8545', nextAccount).then(function (scratchResult) {
                setScratchBalance(scratchResult as string);
            });
        }

        function handleChainIDChanged(chain: string) {
            setChainID(parseInt(chain, 16));
        }

        if (ethereum) {
            ethereum.on('accountsChanged', handleAccountsChanged);
            ethereum.on('chainChanged', handleChainIDChanged);
        }

        return () => {
            if (ethereum) {
                ethereum.removeListener('accountsChanged', handleAccountsChanged);
                ethereum.removeListener('chainChanged', handleChainIDChanged);
            }
        };
    }, [setAccount, setBalance, setChainID, setScratchBalance]);

    const isActiveLink = (href: string) => {
        if (href === '/') {
            return location.pathname === '/' || location.pathname === '/frontpage';
        }

        return location.pathname === href || location.pathname.startsWith(`${href}/`);
    };

    const ExternalBadge = ({
        href,
        label,
        accent,
        compact = false,
    }: {
        href: string;
        label: string;
        accent: 'gold' | 'cyan';
        compact?: boolean;
    }) => (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className={`inline-flex items-center justify-center rounded-full border font-semibold uppercase tracking-[0.18em] transition ${
                compact ? 'px-3 py-2 text-[10px]' : 'px-3 py-2 text-[11px]'
            } ${
                accent === 'gold'
                    ? 'border-[#ffd166]/35 bg-[#ffd166]/10 text-[#fff2cc] hover:bg-[#ffd166]/18'
                    : 'border-[#59d6ff]/35 bg-[#59d6ff]/10 text-[#e8fbff] hover:bg-[#59d6ff]/18'
            }`}
        >
            {label}
        </a>
    );

    const SocialButtons = () => (
        <div className="flex items-center gap-2">
            {socialLinks.map((link) => (
                <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={link.label}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 transition hover:border-white/20 hover:bg-white/10"
                >
                    <img
                        src={webStorage + link.icon}
                        alt={link.label}
                        className="h-3.5 w-3.5 object-contain"
                    />
                </a>
            ))}
        </div>
    );

    const DesktopLink = ({ href, title }: { href: string; title: string }) => (
        <Link
            to={href}
            className={`inline-flex items-center rounded-full px-3 py-2 text-[13px] font-semibold uppercase tracking-[0.1em] transition ${
                isActiveLink(href)
                    ? 'bg-[#59d6ff]/14 text-[#e8fbff] shadow-[inset_0_0_0_1px_rgba(89,214,255,0.28)]'
                    : 'text-[#d3deea] hover:bg-white/10 hover:text-[#fff2cc]'
            }`}
        >
            {title}
        </Link>
    );

    const MobileHeaderLink = ({ href, label }: { href: string; label: string }) => (
        <Link
            to={href}
            className={`inline-flex items-center justify-center rounded-full px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] transition ${
                isActiveLink(href)
                    ? 'bg-[#59d6ff]/14 text-[#f2fbff] shadow-[inset_0_0_0_1px_rgba(89,214,255,0.28)]'
                    : 'text-[#b6c3d0] hover:bg-white/10 hover:text-[#fff2cc]'
            }`}
        >
            {label}
        </Link>
    );

    const LogoBlock = ({
        compact = false,
        showTagline = true,
    }: {
        compact?: boolean;
        showTagline?: boolean;
    }) => (
        <Link to="/" className="flex min-w-0 items-center gap-3">
            <div
                className={`flex shrink-0 items-center justify-center rounded-[20px] border border-[#ffd166]/25 bg-[linear-gradient(135deg,#fff2cc_0%,#ffd166_100%)] ${
                    compact ? 'h-11 w-11' : 'h-14 w-14'
                }`}
            >
                <img
                    src={webStorage + '/navbar/boomersquadlogo2.png'}
                    alt="Boomer Squad"
                    className={`${compact ? 'h-8 w-8' : 'h-11 w-11'} object-contain`}
                />
            </div>
            <div className="min-w-0">
                <div
                    className={`truncate font-brand uppercase leading-none tracking-[0.08em] text-[#fff2cc] ${
                        compact ? 'text-lg' : 'text-[24px]'
                    }`}
                >
                    Boomer Squad
                </div>
                {showTagline ? (
                    <div className="mt-1 truncate text-[10px] uppercase tracking-[0.24em] text-[#59d6ff]">
                        Rooms, Collections, Mattress Fund
                    </div>
                ) : null}
            </div>
        </Link>
    );

    return (
        <>
            <motion.header
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.24, ease: 'easeOut' }}
                className="relative z-20 border-b border-white/10 bg-[rgba(5,8,13,0.94)] shadow-[0_18px_48px_rgba(0,0,0,0.28)] backdrop-blur-2xl"
            >
                <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#59d6ff]/28 to-transparent" />
                <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
                    {isDesktop ? (
                        isCompactDesktop ? (
                            <div className="py-4">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <LogoBlock />
                                    <div className="flex max-w-full flex-wrap items-center justify-end gap-3">
                                        {externalLinks.map((link) => (
                                            <ExternalBadge
                                                key={link.label}
                                                href={link.href}
                                                label={link.label}
                                                accent={link.accent as 'gold' | 'cyan'}
                                            />
                                        ))}
                                        <SocialButtons />
                                        <ConnectModal />
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-wrap items-center justify-center gap-2 border-t border-white/10 pt-4">
                                    {exploreObjects.map((item) => (
                                        <DesktopLink key={item.linkTo} href={item.linkTo} title={item.title} />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="flex min-h-[88px] items-center justify-between gap-4 py-4">
                                <div className="flex min-w-0 items-center gap-4">
                                    <LogoBlock />
                                </div>

                                <div className="flex flex-1 items-center justify-center gap-1 px-2">
                                    {exploreObjects.map((item) => (
                                        <DesktopLink key={item.linkTo} href={item.linkTo} title={item.title} />
                                    ))}
                                </div>

                                <div className="flex shrink-0 items-center gap-2">
                                    {externalLinks.map((link) => (
                                        <ExternalBadge
                                            key={link.label}
                                            href={link.href}
                                            label={link.label}
                                            accent={link.accent as 'gold' | 'cyan'}
                                        />
                                    ))}
                                    <SocialButtons />
                                    <ConnectModal />
                                </div>
                            </div>
                        )
                    ) : (
                        <div className="py-3">
                            <div className="flex items-center justify-between gap-3">
                                <LogoBlock compact showTagline={false} />
                                <div className="shrink-0">
                                    <ConnectModal />
                                </div>
                            </div>
                            <div className="mt-3 border-t border-white/10 pt-3">
                                <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                    {mobileNavItems.map((item) => (
                                        <MobileHeaderLink key={item.href} href={item.href} label={item.label} />
                                    ))}
                                    {externalLinks.map((link) => (
                                        <ExternalBadge
                                            key={link.label}
                                            href={link.href}
                                            label={link.label}
                                            accent={link.accent as 'gold' | 'cyan'}
                                            compact
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </motion.header>
        </>
    );
}

export default NavBar
